// src/components/ProjectNetworkManager.jsx
import React, { useState, useEffect } from "react";
import * as RelationsService from "../services/azureRelationsService";
import * as PartnersService from "../services/azurePartnersService";
import * as PersonsService from "../services/azurePersonsService";
import * as ProjectsService from "../services/azureProjectsService";

const ProjectNetworkManager = ({ project, onClose, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Data states
  const [projectRelations, setProjectRelations] = useState([]);
  const [partners, setPartners] = useState([]);
  const [persons, setPersons] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectSearch, setProjectSearch] = useState("");

  // Form states
  const [activeTab, setActiveTab] = useState("partners");
  const [newRelation, setNewRelation] = useState({
    type: "",
    targetId: "",
    function: "",
    description: "",
    organizationId: "",
  });

  // Search states
  const [partnerSearch, setPartnerSearch] = useState("");
  const [personSearch, setPersonSearch] = useState("");
  const [organizationSearch, setOrganizationSearch] = useState("");
  const [showPartnerSuggestions, setShowPartnerSuggestions] = useState(false);
  const [showPersonSuggestions, setShowPersonSuggestions] = useState(false);
  const [showOrgSuggestions, setShowOrgSuggestions] = useState(false);
  const [showProjectSuggestions, setShowProjectSuggestions] = useState(false);

  // Load data on mount
  useEffect(() => {
    if (project) {
      loadProjectData();
    }
  }, [project]);

  // Clear messages on tab change
  useEffect(() => {
    setError(null);
    setSuccess(null);
    setNewRelation({
      type: "",
      targetId: "",
      function: "",
      description: "",
      organizationId: "",
    });
    setPartnerSearch("");
    setPersonSearch("");
    setOrganizationSearch("");
    setProjectSearch(""); // NEU
    setShowPartnerSuggestions(false);
    setShowPersonSuggestions(false);
    setShowOrgSuggestions(false);
    setShowProjectSuggestions(false); // NEU
  }, [activeTab]);

  if (!project) {
    return <div>Kein Projekt ausgewählt</div>;
  }

  const loadProjectData = async () => {
    try {
      setLoading(true);

      // Use project.code for relations (matches what budget form uses)
      const projectId = project.code || project.id;

      const [relations, partnersData, personsData, projectsData] =
        await Promise.all([
          RelationsService.getProjectRelations(projectId),
          PartnersService.getAllPartners(),
          PersonsService.getAllPersons(),
          ProjectsService.getAllProjects(),
        ]);

      const filteredProjects = projectsData.filter(
        (p) => p.type !== "offerte" && p.id !== project.id && p.code !== project.code
      );

      setProjects(filteredProjects);
      setProjectRelations(relations);
      setPartners(partnersData);
      setPersons(personsData);
    } catch (err) {
      setError("Fehler beim Laden der Projektdaten: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Entferne Duplikat-Prüfung komplett - lasse Benutzer entscheiden

  // Helper: Partner finden oder erstellen
  const findOrCreatePartner = async (searchTerm) => {
    // Erst nach bestehendem Partner suchen
    let partner = partners.find(
      (p) => p.name.toLowerCase() === searchTerm.toLowerCase()
    );

    if (!partner) {
      // Partner erstellen
      const newPartnerData = {
        name: searchTerm,
        type: "Organisation", // Standard-Typ
        address: "",
        website: "",
        description: "Automatisch erstellt über Projekte",
      };

      partner = await PartnersService.createPartner(newPartnerData);

      // Liste aktualisieren
      setPartners((prev) => [...prev, partner]);
    }

    return partner;
  };

  // Helper: Person finden oder erstellen
  const findOrCreatePerson = async (searchTerm) => {
    // Namen aufteilen (Annahme: "Vorname Nachname")
    const nameParts = searchTerm.trim().split(" ");
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || "";

    if (!firstName || !lastName) {
      throw new Error(
        "Bitte Vor- und Nachname eingeben (z.B. 'Max Mustermann')"
      );
    }

    // Erst nach bestehender Person suchen
    let person = persons.find(
      (p) =>
        p.firstName.toLowerCase() === firstName.toLowerCase() &&
        p.lastName.toLowerCase() === lastName.toLowerCase()
    );

    if (!person) {
      // Person erstellen
      const newPersonData = {
        firstName: firstName,
        lastName: lastName,
        email: "",
        phone: "",
        position: "",
        notes: "Automatisch erstellt über Projekte",
      };

      person = await PersonsService.createPerson(newPersonData);

      // Liste aktualisieren
      setPersons((prev) => [...prev, person]);
    }

    return person;
  };

  const handleAddRelation = async (e) => {
    e.preventDefault();

    if (!newRelation.type && activeTab === "partners") {
      setError("Bitte Rolle auswählen");
      return;
    }

    if (!partnerSearch && activeTab === "partners") {
      setError("Bitte Partnerin eingeben");
      return;
    }

    if (!personSearch && activeTab === "persons") {
      setError("Bitte Person eingeben");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let targetEntity;
      let relationData;
      // Use project.code for consistency with budget form
      const projectId = project.code || project.id;

      if (activeTab === "partners") {
        // Partner finden oder erstellen
        targetEntity = await findOrCreatePartner(partnerSearch);

        relationData = {
          sourceType: "project",
          sourceId: projectId,
          targetType: "partner",
          targetId: targetEntity.id,
          relationType: newRelation.type,
          function: newRelation.function,
          description: newRelation.description,
        };
      } else if (activeTab === "persons") {
        // Person finden oder erstellen
        targetEntity = await findOrCreatePerson(personSearch);

        relationData = {
          sourceType: "project",
          sourceId: projectId,
          targetType: "person",
          targetId: targetEntity.id,
          relationType: RelationsService.RELATION_TYPES.PROJECT_PERSON,
          function: newRelation.function,
          description: newRelation.description,
        };

        // Optional: Organisation verknüpfen (nur bestehende!)
        if (organizationSearch) {
          const organization = partners.find(
            (p) => p.name.toLowerCase() === organizationSearch.toLowerCase()
          );

          if (!organization) {
            throw new Error(
              `Organisation "${organizationSearch}" existiert nicht. Bitte zuerst die Organisation erstellen.`
            );
          }

          // Person-Organisation Relation erstellen
          const personOrgRelation = {
            sourceType: "partner",
            sourceId: organization.id,
            targetType: "person",
            targetId: targetEntity.id,
            relationType: RelationsService.RELATION_TYPES.PARTNER_PERSON,
            function: newRelation.function,
            description: "Zuordnung über Projekte",
          };
          await RelationsService.createRelation(personOrgRelation);
        }
      } else if (activeTab === "projects") {
        const selectedProject = projects.find(
          (p) =>
            p.title.toLowerCase() === projectSearch.toLowerCase() ||
            p.code.toLowerCase() === projectSearch.toLowerCase()
        );

        if (!selectedProject) {
          setError("Projekt nicht gefunden");
          return;
        }

        relationData = {
          sourceType: "project",
          sourceId: projectId,
          targetType: "project",
          targetId: selectedProject.code || selectedProject.id,
          relationType: RelationsService.RELATION_TYPES.PROJECT_PROJECT,
          function: newRelation.function,
          description: newRelation.description,
        };
      }

      // Haupt-Relation erstellen
      await RelationsService.createRelation(relationData);

      // Form zurücksetzen
      setNewRelation({
        type: "",
        targetId: "",
        function: "",
        description: "",
        organizationId: "",
      });
      setPartnerSearch("");
      setPersonSearch("");
      setOrganizationSearch("");
      setShowPartnerSuggestions(false);
      setShowPersonSuggestions(false);
      setShowOrgSuggestions(false);

      // Daten neu laden
      await loadProjectData();

      setSuccess("Relation erfolgreich hinzugefügt!");

      if (onSave) {
        onSave();
      }
    } catch (err) {
      setError("Fehler beim Hinzufügen der Relation: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRelation = async (relationId) => {
    if (
      !window.confirm(
        "Sind Sie sicher, dass Sie diese Relation löschen möchten?"
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      await RelationsService.deleteRelation(relationId);
      await loadProjectData();
      setSuccess("Relation erfolgreich gelöscht!");

      if (onSave) {
        onSave();
      }
    } catch (err) {
      setError("Fehler beim Löschen der Relation: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getPartnerName = (partnerId) => {
    const partner = partners.find((p) => p.id === partnerId);
    return partner ? partner.name : "Unbekannt";
  };

  const getPersonName = (personId) => {
    const person = persons.find((p) => p.id === personId);
    return person ? `${person.firstName} ${person.lastName}` : "Unbekannt";
  };

  const getProjectName = (projectId) => {
    const proj = projects.find((p) => p.id === projectId);
    return proj ? proj.title : "Unbekannt";
  };

  const getRelationsByType = (targetType) => {
    return projectRelations.filter((r) => r.targetType === targetType);
  };

  // Filter functions for suggestions
  const getFilteredPartners = () => {
    if (!partnerSearch) return [];
    return partners
      .filter(
        (partner) =>
          partner.name.toLowerCase().includes(partnerSearch.toLowerCase()) ||
          partner.type.toLowerCase().includes(partnerSearch.toLowerCase())
      )
      .slice(0, 5);
  };

  const getFilteredPersons = () => {
    if (!personSearch) return [];
    return persons
      .filter(
        (person) =>
          `${person.firstName} ${person.lastName}`
            .toLowerCase()
            .includes(personSearch.toLowerCase()) ||
          (person.email &&
            person.email.toLowerCase().includes(personSearch.toLowerCase()))
      )
      .slice(0, 5);
  };

  const getFilteredOrganizations = () => {
    if (!organizationSearch) return [];
    return partners
      .filter(
        (partner) =>
          partner.name
            .toLowerCase()
            .includes(organizationSearch.toLowerCase()) ||
          partner.type.toLowerCase().includes(organizationSearch.toLowerCase())
      )
      .slice(0, 5);
  };

  const getFilteredProjects = () => {
    if (!projectSearch) return [];
    return projects
      .filter(
        (p) =>
          p.title.toLowerCase().includes(projectSearch.toLowerCase()) ||
          p.code.toLowerCase().includes(projectSearch.toLowerCase())
      )
      .slice(0, 5);
  };

  // Handle partner selection
  const handlePartnerSelect = (partner) => {
    setPartnerSearch(partner.name);
    setShowPartnerSuggestions(false);
  };

  // Handle person selection
  const handlePersonSelect = (person) => {
    setPersonSearch(`${person.firstName} ${person.lastName}`);
    setShowPersonSuggestions(false);
  };

  // Handle organization selection
  const handleOrganizationSelect = (organization) => {
    setOrganizationSearch(organization.name);
    setShowOrgSuggestions(false);
  };

  const handleProjectSelect = (selectedProject) => {
    setProjectSearch(selectedProject.title);
    setShowProjectSuggestions(false);
  };

  const renderPartnerRelations = () => {
    const partnerRelations = getRelationsByType("partner");

    return (
      <div className="relations-section">
        <h4>Partnerinnen & Auftraggeberinnen</h4>

        <form onSubmit={handleAddRelation} className="add-relation-form">
          <div className="form-row">
            <div className="form-group">
              <label>Partnerin auswählen</label>
              <div className="autocomplete-container">
                <input
                  type="text"
                  value={partnerSearch}
                  onChange={(e) => {
                    setPartnerSearch(e.target.value);
                    setShowPartnerSuggestions(true);
                  }}
                  onFocus={() => setShowPartnerSuggestions(true)}
                  onBlur={() =>
                    setTimeout(() => setShowPartnerSuggestions(false), 200)
                  }
                  placeholder="Organisation eingeben (wird automatisch erstellt falls neu)..."
                  required
                />
                {showPartnerSuggestions && getFilteredPartners().length > 0 && (
                  <div className="suggestions-dropdown">
                    {getFilteredPartners().map((partner) => (
                      <div
                        key={partner.id}
                        className="suggestion-item"
                        onClick={() => handlePartnerSelect(partner)}
                      >
                        <strong>{partner.name}</strong>
                        <span className="suggestion-type">{partner.type}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Rolle</label>
              <select
                value={newRelation.type}
                onChange={(e) =>
                  setNewRelation((prev) => ({ ...prev, type: e.target.value }))
                }
                required
              >
                <option value="">Bitte auswählen...</option>
                <option value={RelationsService.RELATION_TYPES.AUFTRAGGEBERIN}>
                  Auftraggeberin
                </option>
                <option value={RelationsService.RELATION_TYPES.PARTNER}>
                  Partner
                </option>
                <option value={RelationsService.RELATION_TYPES.CONSULTANT}>
                  Consultant
                </option>
                <option value={RelationsService.RELATION_TYPES.CONTRACTOR}>
                  Contractor
                </option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Funktion</label>
              <input
                type="text"
                value={newRelation.function}
                onChange={(e) =>
                  setNewRelation((prev) => ({
                    ...prev,
                    function: e.target.value,
                  }))
                }
                placeholder="z.B. Verkehrsinfrastruktur"
              />
            </div>

            <div className="form-group">
              <label>Beschreibung</label>
              <input
                type="text"
                value={newRelation.description}
                onChange={(e) =>
                  setNewRelation((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="Zusätzliche Informationen"
              />
            </div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            Partnerin hinzufügen
          </button>
        </form>

        <div className="existing-relations">
          {partnerRelations.length === 0 ? (
            <p className="no-relations">Keine Partnerin zugeordnet</p>
          ) : (
            <div>
              {/* Gruppiere Partner nach Namen */}
              {Object.entries(
                partnerRelations.reduce((groups, relation) => {
                  const partnerName = getPartnerName(relation.targetId);
                  if (!groups[partnerName]) {
                    groups[partnerName] = [];
                  }
                  groups[partnerName].push(relation);
                  return groups;
                }, {})
              ).map(([partnerName, relations]) => (
                <div key={partnerName} className="partner-group">
                  <div className="partner-group-header">
                    <strong>{partnerName}</strong>
                  </div>
                  {relations.map((relation) => (
                    <div
                      key={relation.id}
                      className="relation-item relation-sub-item"
                    >
                      <div className="relation-info">
                        <span className="relation-type">
                          {relation.relationType}
                        </span>
                        {relation.function && (
                          <span className="relation-function">
                            {relation.function}
                          </span>
                        )}
                        {relation.description && (
                          <span className="relation-description">
                            {relation.description}
                          </span>
                        )}
                      </div>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeleteRelation(relation.id)}
                        disabled={loading}
                      >
                        X
                      </button>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderProjectRelations = () => {
    const projectRels = getRelationsByType("project");

    return (
      <div className="relations-section">
        <h4>Projekte verlinken</h4>

        <form onSubmit={handleAddRelation} className="add-relation-form">
          <div className="form-row">
            <div className="form-group autocomplete-container">
              <label>Projekt</label>
              <input
                type="text"
                value={projectSearch}
                onChange={(e) => {
                  setProjectSearch(e.target.value);
                  setShowProjectSuggestions(true);
                }}
                onFocus={() => setShowProjectSuggestions(true)}
                onBlur={() =>
                  setTimeout(() => setShowProjectSuggestions(false), 200)
                }
                placeholder="Projektname oder Nummer"
                required
              />
              {showProjectSuggestions && getFilteredProjects().length > 0 && (
                <div className="suggestions-dropdown">
                  {getFilteredProjects().map((proj) => (
                    <div
                      key={proj.id}
                      className="suggestion-item"
                      onClick={() => handleProjectSelect(proj)}
                    >
                      <strong>{proj.title}</strong>
                      <span className="suggestion-type">
                        {proj.code}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Funktion</label>
              <input
                type="text"
                value={newRelation.function}
                onChange={(e) =>
                  setNewRelation((prev) => ({
                    ...prev,
                    function: e.target.value,
                  }))
                }
                placeholder="z.B. Folgeprojekt, Teilprojekt"
              />
            </div>

            <div className="form-group">
              <label>Beschreibung</label>
              <input
                type="text"
                value={newRelation.description}
                onChange={(e) =>
                  setNewRelation((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="Zusätzliche Informationen"
              />
            </div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            Projekt hinzufügen
          </button>
        </form>

        <div className="existing-relations">
          {projectRels.length === 0 ? (
            <p className="no-relations">Keine Projekte verlinkt</p>
          ) : (
            projectRels.map((relation) => (
              <div key={relation.id} className="relation-item">
                <div className="relation-info">
                  <strong>{getProjectName(relation.targetId)}</strong>
                  {relation.function && (
                    <span className="relation-function">
                      {relation.function}
                    </span>
                  )}
                </div>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => handleDeleteRelation(relation.id)}
                  disabled={loading}
                >
                  X
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  const renderPersonRelations = () => {
    const personRelations = getRelationsByType("person");

    return (
      <div className="relations-section">
        <h4>Personen</h4>

        <form onSubmit={handleAddRelation} className="add-relation-form">
          <div className="form-row">
            <div className="form-group">
              <label>Name</label>
              <div className="autocomplete-container">
                <input
                  type="text"
                  value={personSearch}
                  onChange={(e) => {
                    setPersonSearch(e.target.value);
                    setShowPersonSuggestions(true);
                  }}
                  onFocus={() => setShowPersonSuggestions(true)}
                  onBlur={() =>
                    setTimeout(() => setShowPersonSuggestions(false), 200)
                  }
                  placeholder="Vor- und Nachname eingeben (wird automatisch erstellt falls neu)..."
                  required
                />
                {showPersonSuggestions && getFilteredPersons().length > 0 && (
                  <div className="suggestions-dropdown">
                    {getFilteredPersons().map((person) => (
                      <div
                        key={person.id}
                        className="suggestion-item"
                        onClick={() => handlePersonSelect(person)}
                      >
                        <strong>
                          {person.firstName} {person.lastName}
                        </strong>
                        {person.email && (
                          <span className="suggestion-email">
                            {person.email}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Organisation (optional)</label>
              <div className="autocomplete-container">
                <input
                  type="text"
                  value={organizationSearch}
                  onChange={(e) => {
                    setOrganizationSearch(e.target.value);
                    setShowOrgSuggestions(true);
                  }}
                  onFocus={() => setShowOrgSuggestions(true)}
                  onBlur={() =>
                    setTimeout(() => setShowOrgSuggestions(false), 200)
                  }
                  placeholder="Organisation eingeben (muss bereits existieren)..."
                />
                {showOrgSuggestions &&
                  getFilteredOrganizations().length > 0 && (
                    <div className="suggestions-dropdown">
                      {getFilteredOrganizations().map((org) => (
                        <div
                          key={org.id}
                          className="suggestion-item"
                          onClick={() => handleOrganizationSelect(org)}
                        >
                          <strong>{org.name}</strong>
                          <span className="suggestion-type">{org.type}</span>
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Funktion</label>
              <input
                type="text"
                value={newRelation.function}
                onChange={(e) =>
                  setNewRelation((prev) => ({
                    ...prev,
                    function: e.target.value,
                  }))
                }
                placeholder="z.B. Projektleiter, Architekt"
              />
            </div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            Person hinzufügen
          </button>
        </form>

        <div className="existing-relations">
          {personRelations.length === 0 ? (
            <p className="no-relations">Keine Personen zugeordnet</p>
          ) : (
            personRelations.map((relation) => (
              <div key={relation.id} className="relation-item">
                <div className="relation-info">
                  <strong>{getPersonName(relation.targetId)}</strong>
                  {relation.function && (
                    <span className="relation-function">
                      {relation.function}
                    </span>
                  )}
                </div>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => handleDeleteRelation(relation.id)}
                  disabled={loading}
                >
                  X
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="project-network-manager">
      <div className="manager-header">
        <h2>Netzwerk: {project.title}</h2>
        <button className="btn btn-secondary" onClick={onClose}>
          X
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="tabs">
        <button
          className={`tab ${activeTab === "partners" ? "active" : ""}`}
          onClick={() => setActiveTab("partners")}
        >
          Partnerinnen
        </button>
        <button
          className={`tab ${activeTab === "persons" ? "active" : ""}`}
          onClick={() => setActiveTab("persons")}
        >
          Personen
        </button>
        <button
          className={`tab ${activeTab === "projects" ? "active" : ""}`}
          onClick={() => setActiveTab("projects")}
        >
          Projekte
        </button>
      </div>

      <div className="tab-content">
        {loading && <div className="loading">Lade...</div>}

        {!loading && (
          <>
            {activeTab === "partners" && renderPartnerRelations()}
            {activeTab === "persons" && renderPersonRelations()}
            {activeTab === "projects" && renderProjectRelations()}
          </>
        )}
      </div>
    </div>
  );
};

export default ProjectNetworkManager;
